import { createContext, useState, useContext, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import keikkaClass from "src/data/keikka/keikkaClass";

import { timeDiff } from "src/components/js/statics1";
import {
  loadKeikkaData_Auth,
  loadKeikkaData_Betoni_Main,
  loadKeikkaData_Pumpparit,
  loadKeikkaData_asiakasPersonSettings,
  loadKeikkaData_betoniLisät,
  loadKeikkaData_finalize,
  loadKeikkaData_init,
} from "src/data/keikka/loadKeikkaData";
import { useUserData } from "./userContext";
import { useNavData } from "./navContext";

export const KeikkaDataContext = createContext();
const stepId_määrä = 0;
const stepId_osoite = 1;
const stepId_pumppu = 2;
const stepId_aika = 3;
const stepId_betoni = 4;
// eslint-disable-next-line no-unused-vars
const stepId_lattia = 5;
const stepId_yhteenveto = 6;
const stepId_process = 7;
const stepId_toimittajaSelector = 8;
const stepId_betoniM3 = 9;
const stepId_betoniJaM3 = 10;

export const KeikkaDataProvider = ({ children, keikkaId }) => {
  const { user, token, socket } = useUserData();
  const { navToToimittajaSelector } = useNavData();
  const [origKeikkaData, setOrigKeikkaData] = useState({
    keikkaData: null,
    varmenne: null,
  });
  const [keikkaData, setKeikkaData] = useState([]);
  const [, setUniqueHash] = useState();
  const [maksajaAsiakas, setMaksajaAsiakas] = useState(null);
  const [laskuttajaAsiakas, setLaskuttajaAsiakas] = useState(null);
  const [laskuttajaType, setLaskuttajaType] = useState("pumppu");
  const [keikkaDataStatus, setKeikkaDataStatus] = useState({
    isLoaded: false,
    error: null,
    state: "initial",
  });
  const [isKeikkaDataLoaded, setIsKeikkaDataLoaded] = useState({
    start: false,
    init: false,
    auth: false,
    betonit: false,
    finalize: false,
    asiakasPersonSettings: false,
    pumpparit: false,
  });

  const isLoadedPercent = {
    percent: Math.round(
      (Object.values(isKeikkaDataLoaded).filter((v) => v).length /
        Object.keys(isKeikkaDataLoaded).length) *
        100
    ),
    count: Object.keys(isKeikkaDataLoaded).length,
  };
  const isAllLoaded = isLoadedPercent.percent === 100;
  const isLoaded = isLoadedPercent.percent === 100;

  // connect sockets
  useEffect(() => {
    if (!socket || !socket.connected) return;
    if (keikkaId == 0 || keikkaId == null) return;
    if (!user) return;
    console.log("JOIN ROOMS and start listeners");
    socket.emit("join", keikkaId);
    socket.on("onSave", async () => {
      console.log("heard change, apply changes");
      const _keikkaData = await loadKeikkaData(keikkaData, user, token);
      doSetKeikkaData(_keikkaData);
    });
    socket.on("disconnect", () => {
      console.log("socket disconnected in steps useEffect");
    });
    socket.on("reconnect", () => {
      console.log("socket reconnected in steps");
      socket.emit("join", keikkaId);
      socket.emit("join", "pid" + user.personId);
      socket.emit("connCount");
      socket.emit("keikkaConnCOunt", keikkaId);
    });
    // socket.on("cache_clear", (cacheName) => {
    //   cache_clear(cacheName);
    // });

    return () => {
      // reset keikkaData to loading, so when soft navigating, loading is shown
      if (keikkaData) keikkaData.isLoading = true;
      setKeikkaData(keikkaData);
      // remove listeners
      if (socket) {
        console.log("Leave rooms and remove listeners");
        socket.emit("leave", keikkaId); // leave room
        socket.emit("keikkaconncount", keikkaId); // update active usercount for this keikka for all online users (number of people in this room)
        socket.off("onSave");
        socket.off("authenticated");
        socket.off("connect", () => socket.emit("leave", keikkaId));
        socket.off("disconnect", () => console.log("socket disconnected in steps useEffect"));
        socket.off("reconnect");
      } else {
        console.log("cannot leave rooms and stop listeners, socket not found");
      }
    };
  }, [keikkaId, user, socket, socket.connected]);

  useEffect(() => {
    const _asynk = async () => {
      if (!keikkaId || !token) {
        return;
      } else {
        // create keikkaData
        setKeikkaDataStatus({ isLoaded: false, error: null, state: "loading keikkaDatalist" });
        var keikkaData = new keikkaClass(user.personId, keikkaId);
        keikkaData = await loadKeikkaData(keikkaData, user, token);
        doSetKeikkaData(keikkaData);
        const _laskuttajaAsiakas = keikkaData.laskutus.getLaskuttajaAsiakas(
          keikkaData,
          laskuttajaType
        );
        const _maksajaAsiakas = keikkaData.laskutus.getMaksajaAsiakas(keikkaData, laskuttajaType);
        setMaksajaAsiakas(_maksajaAsiakas);
        setKeikkaDataStatus({ isLoaded: true, error: null, state: "success" });
        setLaskuttajaAsiakas(_laskuttajaAsiakas);
      }
    };
    _asynk();
  }, [keikkaId]);

  // When osoite changes, check pumppu and betonitoimittajat
  useEffect(() => {
    if (!isAllLoaded) return;
    if (keikkaData.tyomaa.tyomaaOsoite1.length < 5) return;
    if (
      keikkaData?.pumppuToimittaja?.asiakasId === 0 ||
      keikkaData?.betoniToimittaja?.asiakasId === 0
    ) {
      navToToimittajaSelector();
    }
  }, [
    keikkaData?.tyomaa?.tyomaaOsoite1,
    keikkaData?.tyomaa?.tyomaaOsoite2,
    keikkaData?.tyomaa?.tyomaaOsoite3,
    keikkaData?.tyomaa?.tyomaaOsoite4,
    keikkaData?.pumppuToimittaja?.asiakasId,
    keikkaData?.betoniToimittaja?.asiakasId,
    isAllLoaded,
  ]);

  const doSetLaskuttajaType = useCallback(
    (laskuttajaType) => {
      setLaskuttajaType(laskuttajaType);
      const laskuttajaAsiakas = keikkaData.laskutus.getLaskuttajaAsiakas(
        keikkaData,
        laskuttajaType
      );
      doSetLaskuttajaAsiakas(laskuttajaAsiakas);
      const maksajaAsiakas = keikkaData.laskutus.getMaksajaAsiakas(keikkaData, laskuttajaType);
      setMaksajaAsiakas(maksajaAsiakas);
    },
    [
      laskuttajaType,
      keikkaData?.asiakas?.asiakasId,
      keikkaData?.pumppuToimittaja?.asiakasId,
      keikkaData?.betoniToimittaja?.asiakasId,
      keikkaData?.lattiaToimittaja?.asiakasId,
    ]
  );

  const doSetLaskuttajaAsiakas = useCallback(
    (asiakas) => {
      setLaskuttajaAsiakas(asiakas);
    },
    [laskuttajaAsiakas, laskuttajaAsiakas?.asiakasHash]
  );

  // Update keikkaData state. This will update the screen with up-to-date info.
  // To notice a change in state, use uniqueHash
  const doSetKeikkaData = useCallback(
    (keikkaData) => {
      setKeikkaData(keikkaData);
      setUniqueHash(keikkaData.uniqueHash); // THIS IS THE FORCE UPDATE
    },
    [keikkaData && keikkaData.uniqueHash]
  );
  // Set origKeikkaData state. This will be used to check if keikkaData has changed
  const doSetOrigKeikkaData = useCallback(
    (_keikkaData) => {
      setOrigKeikkaData({
        keikkaData: JSON.parse(_keikkaData.json()),
        varmenne: _keikkaData.varmenne,
      });
    },
    [keikkaData && keikkaData.varmenne]
  );

  async function loadKeikkaData(_keikkaData, user, token, setState = true) {
    if (setState) {
      setIsKeikkaDataLoaded((prev) => ({
        ...prev,
        start: true,
        init: false,
        auth: false,
        betonit: false,
        asiakasPersonSettings: false,
      }));
      _keikkaData.isBackgroundLoading = true;
      doSetKeikkaData(_keikkaData);
    }
    var first = new Date().getTime();
    // Initialize
    const a = await init();
    if (a?.status === "error") {
      _keikkaData.isLoading = false;
      _keikkaData.isBackgroundLoading = false;
      _keikkaData.errors.errorTxt = a.errorTxt;
      _keikkaData.errors.errorNum = a.errorNum;
      doSetKeikkaData(_keikkaData);
      return _keikkaData;
    }
    // Authorize
    await auth();

    // Check auth
    if (user.isSystemAdmin) {
      console.log("IS SYSADMIN", _keikkaData);
    }
    if (!_keikkaData.auth.read) {
      _keikkaData = new keikkaClass(user.personId);
      console.log("no read access");
      _keikkaData.errors.errorTxt = "Ei pääsyä tilaukselle.";
      _keikkaData.errors.errorNum = 403;
      _keikkaData.isLoading = false;
      _keikkaData.isBackgroundLoading = false;
      doSetKeikkaData(_keikkaData);
      return _keikkaData;
    }
    doSetKeikkaData(_keikkaData);
    await Promise.all([betonit(), asiakasPersonSettings(), pumpparit()]).then(() => {
      finalize()
        .then(() => {
          setIsKeikkaDataLoaded((prev) => ({ ...prev, finalize: true }));
          console.log("finalize", timeDiff(first), "ms");
          _keikkaData.isBackgroundLoading = false;
          doSetKeikkaData(_keikkaData);
          doSetOrigKeikkaData(_keikkaData);
        })
        .catch((err) => {
          console.log("finalize error", err);
        });
    });

    async function init() {
      var start = new Date().getTime();
      const a = await loadKeikkaData_init(_keikkaData, user, token, socket);
      setIsKeikkaDataLoaded((prev) => ({ ...prev, init: true, initMs: timeDiff(start) }));
      _keikkaData.isLoading = false;
      console.log("init", timeDiff(start), "ms");
      return a;
    }

    async function auth() {
      var start = new Date().getTime();
      await loadKeikkaData_Auth(_keikkaData, user, token).then(() => {
        setIsKeikkaDataLoaded((prev) => ({ ...prev, auth: true, authMs: timeDiff(start) }));
        console.log("auth", timeDiff(start), "ms");
      });
    }
    async function asiakasPersonSettings() {
      var start = new Date().getTime();
      await loadKeikkaData_asiakasPersonSettings(_keikkaData, user, token).then(() => {
        setIsKeikkaDataLoaded((prev) => ({
          ...prev,
          asiakasPersonSettings: true,
          asiakasPersonSettingsMs: timeDiff(start),
        }));
        console.log("asiakasPersonSettings", timeDiff(start), "ms");
      });
    }

    async function betonit() {
      var start = new Date().getTime();
      if (_keikkaData.modules.hasBetoni) await loadKeikkaData_Betoni_Main(_keikkaData, user, token);
      setIsKeikkaDataLoaded((prev) => ({ ...prev, betonit: true, betonitMs: timeDiff(start) }));
      console.log("betonit", timeDiff(start), "ms");

      start = new Date().getTime();
      if (_keikkaData.modules.hasBetoni) await loadKeikkaData_betoniLisät(_keikkaData, user, token);
      setIsKeikkaDataLoaded((prev) => ({
        ...prev,
        betoniLisät: true,
        betoniLisätMs: timeDiff(start),
      }));
      console.log("betoni lisät", timeDiff(start), "ms");
    }

    async function pumpparit() {
      var start = new Date().getTime();
      await loadKeikkaData_Pumpparit(_keikkaData, token);
      setIsKeikkaDataLoaded((prev) => ({ ...prev, pumpparit: true, pumpparitMs: timeDiff(start) }));
      console.log("pumpparit", timeDiff(start), "ms");
    }

    async function finalize() {
      var start = new Date().getTime();
      await loadKeikkaData_finalize(_keikkaData, user, token);
      setIsKeikkaDataLoaded((prev) => ({ ...prev, finalize: true, finalizeMs: timeDiff(start) }));
    }

    return _keikkaData;
  }

  const doSetKeikkaDataForStep = useCallback(
    (keikkaData, stepId) => {
      switch (stepId) {
        case stepId_määrä:
          setUniqueHash(keikkaData.totM3);
          break;
        case stepId_osoite:
          setUniqueHash(keikkaData.asiakas.asiakasHash + keikkaData.tyomaa.tyomaaHash);
          break;
        case stepId_pumppu:
          setUniqueHash(keikkaData.pumppu.pumppuHash);
          break;
        case stepId_aika:
          setUniqueHash(keikkaData.pumppu.pumppuHash);
          break;
        case stepId_betoni:
          setUniqueHash(keikkaData.betoniHash);
          break;
        case stepId_yhteenveto:
          doSetKeikkaData(keikkaData);
          break;
        case stepId_process:
          doSetKeikkaData(keikkaData);
          break;
        case stepId_toimittajaSelector:
          setUniqueHash(
            keikkaData.betoniToimittaja.asiakasId + keikkaData.pumppuToimittaja.asiakasId
          );
          break;
        case stepId_betoniM3:
          setUniqueHash(keikkaData.totM3);
          break;
        case stepId_betoniJaM3:
          setUniqueHash(keikkaData.betoniHash);
          break;
        default:
          doSetKeikkaData(keikkaData);
          break;
      }
      setKeikkaData(keikkaData);
      setUniqueHash(keikkaData.uniqueHash); // THIS IS THE FORCE UPDATE
    },
    [keikkaData && keikkaData.uniqueHash]
  );

  // Check to see if keikkaData has changed
  const isChanged = () => {
    const _isChanged = keikkaData.varmenne !== origKeikkaData.varmenne;
    if (_isChanged)
      console.log("change detected:" + origKeikkaData.varmenne + "->" + keikkaData.varmenne);
    return _isChanged;
  };

  const reloadKeikkaData = async () => {
    const _keikkaData = await loadKeikkaData(keikkaData, user, token);
    doSetKeikkaData(_keikkaData);
  };

  const resetLoadingState = () => {
    setIsKeikkaDataLoaded({
      start: false,
      init: false,
      auth: false,
      pToimittaja: false,
      bToimittaja: false,
      lToimittaja: false,
      betonit: false,
      finalize: false,
      asiakasPersonSettings: false,
      //vehicle: false,
      pumpparit: false,
    });
  };

  return (
    <KeikkaDataContext.Provider
      value={{
        keikkaData,
        doSetKeikkaData,
        doSetKeikkaDataForStep,
        keikkaDataStatus,
        isChanged,
        isLoadedPercent,
        isLoaded,
        laskuttajaAsiakas,
        maksajaAsiakas,
        laskuttajaType,
        doSetLaskuttajaType,
        origKeikkaData,
        doSetOrigKeikkaData,
        reloadKeikkaData,
        isKeikkaDataLoaded,
        resetLoadingState,
      }}>
      {children}
    </KeikkaDataContext.Provider>
  );
};

export const useKeikkaData = () => useContext(KeikkaDataContext);

KeikkaDataProvider.propTypes = {
  token: PropTypes.string,
  children: PropTypes.node.isRequired,
  keikkaId: PropTypes.number,
  socket: PropTypes.object,
};
